import React, { useCallback, useEffect, useState } from "react";
import { FormControlLabel, Switch, TextField } from "@mui/material";
import withStyles from "isomorphic-style-loader/withStyles";
import ReactTooltip from "react-tooltip";
import styles from "./styles.scss";
import dashboardStyles from "../../styles.scss";
import {
  serviceCaller,
  ServiceCallerProps,
} from "../../../../../../composition";
import IGlobalConfig from "../../../../../../../types/IGlobalConfig";
import { updateDashboardOtherSetting } from "../../../../../../../utils/fetch";
import SegmentedToggle from "../../../../../integrations/common/segmentedToggle";

export type DefaultMessageType = "onlySms" | "onlyMms";

export type CharitySettingField =
  | "enableVirtuous"
  | "enableDonorPerfect"
  | "donorPerfectEnableInboundGiftNotifications"
  | "enablePostGiveButtonLabel"
  | "enablePostGiveButtonUrl"
  | "enableMotivationCode"
  | "enableEmbeddedPost"
  | "enableInformativePost"
  | "enableTemporarySegments"
  | "enableTimeRestriction"
  | "enableGifPreview"
  | "emailPostNotifications"
  | "emailPostNotificationsDefault"
  | "smsPostNotifications"
  | "smsPostNotificationsDefault"
  | "enableOnlySmsPost"
  | "enableThankYouFeature"
  | "disableThankYouDonationEmail"
  | "enableBillingFailureAlerts"
  | "emailLookupEnabled"
  | "enableAdminMetrics"
  | "adminPushNotifications";

export interface CharitySettingsValues {
  enableVirtuous: boolean;
  enableDonorPerfect: boolean;
  donorPerfectEnableInboundGiftNotifications: boolean;
  enablePostGiveButtonLabel: boolean;
  enablePostGiveButtonUrl: boolean;
  enableMotivationCode: boolean;
  enableEmbeddedPost: boolean;
  enableInformativePost: boolean;
  enableTemporarySegments: boolean;
  enableTimeRestriction: boolean;
  enableGifPreview: boolean;
  emailPostNotifications: boolean;
  emailPostNotificationsDefault: boolean;
  smsPostNotifications: boolean;
  smsPostNotificationsDefault: boolean;
  enableOnlySmsPost: boolean;
  defaultMessageType: DefaultMessageType;
  enableThankYouFeature: boolean;
  disableThankYouDonationEmail: boolean;
  enableBillingFailureAlerts: boolean;
  emailLookupEnabled: boolean;
  enableAdminMetrics: boolean;
  adminPushNotifications: boolean;
  isVirtuousConnected: boolean;
  isDonorPerfectConnected: boolean;
  maxVideoLength: number;
}

interface SettingDefinition {
  field: CharitySettingField;
  label: string;
  getChecked: (values: CharitySettingsValues) => boolean;
  getWriteValue?: (checked: boolean) => boolean;
  isLocked?: (values: CharitySettingsValues) => boolean;
}

interface SettingGroup {
  id: string;
  title: string;
  settings: SettingDefinition[];
}

const SETTING_GROUPS: SettingGroup[] = [
  {
    id: "integrations",
    title: "Integrations",
    settings: [
      {
        field: "enableVirtuous",
        label: "Virtuous",
        getChecked: values => values.enableVirtuous,
        isLocked: values => values.isVirtuousConnected,
      },
      {
        field: "enableDonorPerfect",
        label: "DonorPerfect",
        getChecked: values => values.enableDonorPerfect,
        isLocked: values => values.isDonorPerfectConnected,
      },
      {
        field: "donorPerfectEnableInboundGiftNotifications",
        label: "Notify incoming DonorPerfect gifts",
        getChecked: values => values.donorPerfectEnableInboundGiftNotifications,
        isLocked: values => !values.enableDonorPerfect,
      },
    ],
  },
  {
    id: "post-composer",
    title: "Post composer",
    settings: [
      {
        field: "enablePostGiveButtonLabel",
        label: "Enable changing Give button label",
        getChecked: values => values.enablePostGiveButtonLabel,
      },
      {
        field: "enablePostGiveButtonUrl",
        label: "Enable changing Give button URL",
        getChecked: values => values.enablePostGiveButtonUrl,
      },
      {
        field: "enableMotivationCode",
        label: "Enable motivation codes",
        getChecked: values => values.enableMotivationCode,
      },
    ],
  },
  {
    id: "post-types",
    title: "Post types & delivery",
    settings: [
      {
        field: "enableEmbeddedPost",
        label: "Enable embedded posts",
        getChecked: values => values.enableEmbeddedPost,
      },
      {
        field: "enableInformativePost",
        label: "Enable informative posts",
        getChecked: values => values.enableInformativePost,
      },
      {
        field: "enableTemporarySegments",
        label: "Enable temporary segments",
        getChecked: values => values.enableTemporarySegments,
      },
      {
        field: "enableTimeRestriction",
        label: "Enable schedule time restrictions",
        getChecked: values => values.enableTimeRestriction,
      },
      {
        field: "enableGifPreview",
        label: "Enable GIF previews",
        getChecked: values => values.enableGifPreview,
      },
    ],
  },
  {
    id: "notifications",
    title: "Notifications & messaging",
    settings: [
      {
        field: "emailPostNotifications",
        label: "Enable sending Email posts",
        getChecked: values => values.emailPostNotifications,
      },
      {
        field: "emailPostNotificationsDefault",
        label: "Default email notifications on new posts",
        getChecked: values => values.emailPostNotificationsDefault,
      },
      {
        field: "smsPostNotifications",
        label: "Enable sending MMS posts",
        getChecked: values => values.smsPostNotifications,
      },
      {
        field: "smsPostNotificationsDefault",
        label: "Default MMS notifications on new posts",
        getChecked: values => values.smsPostNotificationsDefault,
      },
      {
        field: "enableOnlySmsPost",
        label: "Allow SMS sending",
        getChecked: values => values.enableOnlySmsPost,
      },
      {
        field: "adminPushNotifications",
        label: "Admin app push notifications",
        getChecked: values => values.adminPushNotifications,
      },
    ],
  },
  {
    id: "thank-you",
    title: "Thank-you & donations",
    settings: [
      {
        field: "enableThankYouFeature",
        label: "Enable thank-you feature",
        getChecked: values => values.enableThankYouFeature,
      },
      {
        field: "disableThankYouDonationEmail",
        label: "Enable thank-you donation emails",
        getChecked: values => !values.disableThankYouDonationEmail,
        getWriteValue: checked => !checked,
      },
      {
        field: "enableBillingFailureAlerts",
        label: "Enable billing failure alerts",
        getChecked: values => values.enableBillingFailureAlerts,
      },
    ],
  },
  {
    id: "donor-data",
    title: "Donor data",
    settings: [
      {
        field: "emailLookupEnabled",
        label: "Enable email lookup on import",
        getChecked: values => values.emailLookupEnabled,
      },
    ],
  },
  {
    id: "admin-portal",
    title: "Admin portal",
    settings: [
      {
        field: "enableAdminMetrics",
        label: "Metrics dashboard (Overview)",
        getChecked: values => values.enableAdminMetrics,
      },
    ],
  },
];

interface Props extends ServiceCallerProps {
  charityId: string;
  config?: IGlobalConfig;
  configLoaded: boolean;
  settings: CharitySettingsValues;
}

const OtherSettings: React.FC<Props> = ({
  charityId,
  config,
  configLoaded,
  settings,
  serviceAccessToken,
  isSuperadmin,
}) => {
  const [updatingField, setUpdatingField] = useState<CharitySettingField | null>(
    null
  );
  const [updatingDefaultMessageType, setUpdatingDefaultMessageType] =
    useState(false);
  const [updatingVideoLength, setUpdatingVideoLength] = useState(false);
  const [videoLengthDraft, setVideoLengthDraft] = useState(
    String(settings.maxVideoLength)
  );

  useEffect(() => {
    setVideoLengthDraft(String(settings.maxVideoLength));
  }, [settings.maxVideoLength]);

  const handleToggle = useCallback(
    async (
      field: CharitySettingField,
      checked: boolean,
      writeValue: boolean = checked
    ) => {
      const adminServiceUrl = config?.adminServiceUrl;
      if (!charityId || !adminServiceUrl || !serviceAccessToken) {
        return;
      }

      setUpdatingField(field);
      try {
        await updateDashboardOtherSetting(
          charityId,
          field,
          writeValue,
          adminServiceUrl,
          serviceAccessToken
        );
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
        window.alert(
          error instanceof Error
            ? error.message
            : "Could not update this setting. Please try again."
        );
      } finally {
        setUpdatingField(null);
      }
    },
    [charityId, config?.adminServiceUrl, serviceAccessToken]
  );

  const handleDefaultMessageTypeChange = useCallback(
    async (value: string) => {
      const adminServiceUrl = config?.adminServiceUrl;
      if (!charityId || !adminServiceUrl || !serviceAccessToken) {
        return;
      }

      setUpdatingDefaultMessageType(true);
      try {
        await updateDashboardOtherSetting(
          charityId,
          "defaultMessageType",
          null,
          adminServiceUrl,
          serviceAccessToken,
          undefined,
          value
        );
      } catch (error) {
        console.error("Failed to update defaultMessageType:", error);
        window.alert(
          error instanceof Error
            ? error.message
            : "Could not update default message type. Please try again."
        );
      } finally {
        setUpdatingDefaultMessageType(false);
      }
    },
    [charityId, config?.adminServiceUrl, serviceAccessToken]
  );

  const handleVideoLengthSave = useCallback(async () => {
    const adminServiceUrl = config?.adminServiceUrl;
    if (!charityId || !adminServiceUrl || !serviceAccessToken || !isSuperadmin) {
      return;
    }

    const parsed = Number.parseInt(videoLengthDraft, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      window.alert("Video duration must be a positive whole number of seconds.");
      setVideoLengthDraft(String(settings.maxVideoLength));
      return;
    }

    if (parsed === settings.maxVideoLength) {
      return;
    }

    setUpdatingVideoLength(true);
    try {
      await updateDashboardOtherSetting(
        charityId,
        "maxVideoLength",
        null,
        adminServiceUrl,
        serviceAccessToken,
        parsed
      );
    } catch (error) {
      console.error("Failed to update maxVideoLength:", error);
      window.alert(
        error instanceof Error
          ? error.message
          : "Could not update video duration. Please try again."
      );
      setVideoLengthDraft(String(settings.maxVideoLength));
    } finally {
      setUpdatingVideoLength(false);
    }
  }, [
    charityId,
    config?.adminServiceUrl,
    isSuperadmin,
    serviceAccessToken,
    settings.maxVideoLength,
    videoLengthDraft,
  ]);

  const controlsDisabled =
    !configLoaded ||
    !config?.adminServiceUrl ||
    !serviceAccessToken ||
    !isSuperadmin;

  const renderSetting = (setting: SettingDefinition) => {
    const isDisabled =
      controlsDisabled ||
      updatingField === setting.field ||
      Boolean(setting.isLocked?.(settings));

    return (
      <div key={setting.field} className={styles.settingRow}>
        <span className={styles.settingLabel}>{setting.label}</span>
        <FormControlLabel
          className={styles.switchControl}
          control={
            <Switch
              checked={setting.getChecked(settings)}
              disabled={isDisabled}
              onChange={(_, checked) =>
                handleToggle(
                  setting.field,
                  checked,
                  setting.getWriteValue
                    ? setting.getWriteValue(checked)
                    : checked
                )
              }
              color="primary"
            />
          }
          label=""
        />
      </div>
    );
  };

  const renderPostTypesGroup = (group: SettingGroup) => (
    <div key={group.id} className={dashboardStyles.charitySettingsColumn}>
      <h6 className={styles.groupTitle}>{group.title}</h6>
      <div className={styles.settingsList}>
        {group.settings.map(renderSetting)}
        <div className={styles.settingRow}>
          <span className={styles.settingLabel}>Video duration (seconds)</span>
          <TextField
            className={styles.numberInput}
            type="number"
            size="small"
            inputProps={{ min: 1, step: 1 }}
            value={videoLengthDraft}
            disabled={controlsDisabled || updatingVideoLength}
            onChange={event => setVideoLengthDraft(event.target.value)}
            onBlur={handleVideoLengthSave}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleVideoLengthSave();
              }
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderNotificationsGroup = (group: SettingGroup) => (
    <div key={group.id} className={dashboardStyles.charitySettingsColumn}>
      <h6 className={styles.groupTitle}>{group.title}</h6>
      <div className={styles.settingsList}>
        {group.settings.map(renderSetting)}
        {settings.enableOnlySmsPost && (
          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>
              Default message type
              <span
                className={styles.settingInfo}
                data-tip
                data-for="tpDefaultMessageType"
              >
                <i className="fa fa-question-circle" />
              </span>
            </span>
            <SegmentedToggle
              title=""
              hideTitle
              value={settings.defaultMessageType}
              options={[
                { value: "onlySms", label: "SMS" },
                { value: "onlyMms", label: "MMS" },
              ]}
              onChange={handleDefaultMessageTypeChange}
              disabled={controlsDisabled || updatingDefaultMessageType}
              compact
              className={styles.segmentedControl}
            />
            <ReactTooltip
              id="tpDefaultMessageType"
              type="info"
              place="bottom"
              effect="float"
            >
              Default post type for new ViewSparks when SMS-only posts are
              allowed.
            </ReactTooltip>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h6 className={styles.title}>Charity Settings</h6>
      </div>

      <div className={dashboardStyles.charitySettingsColumns}>
        {SETTING_GROUPS.map(group =>
          group.id === "post-types" ? (
            renderPostTypesGroup(group)
          ) : group.id === "notifications" ? (
            renderNotificationsGroup(group)
          ) : (
            <div key={group.id} className={dashboardStyles.charitySettingsColumn}>
              <h6 className={styles.groupTitle}>{group.title}</h6>
              <div className={styles.settingsList}>
                {group.settings.map(renderSetting)}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default serviceCaller(withStyles(styles, dashboardStyles)(OtherSettings));
